
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { MoreVertical, Edit, Trash2, ExternalLink, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DerivativeItem } from './types';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { SocialLinks } from '@/components/creator/SocialLinks';
import { useTheme } from '@/contexts/ThemeContext';

interface DerivativeRowProps {
  derivative: DerivativeItem;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const DerivativeRow: React.FC<DerivativeRowProps> = ({
  derivative,
  onEdit,
  onDelete
}) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  
  const getStatusBadge = () => {
    switch (derivative.status) {
      case 'approved':
        return <Badge className="ml-2 bg-green-500 hover:bg-green-600">Approved</Badge>;
      case 'pending':
        return <Badge variant="outline" className="ml-2">Pending</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="ml-2">Rejected</Badge>;
      default:
        return null;
    }
  };
  
  const getDateInfo = () => {
    if (derivative.approvedDate) {
      return `Approved: ${derivative.approvedDate}`;
    } else if (derivative.rejectedDate) {
      return `Rejected: ${derivative.rejectedDate}`;
    } else {
      return `Submitted: ${derivative.submittedDate}`;
    }
  };
  
  return (
    <Card className="mb-3 hover:bg-muted/50 transition-colors">
      <div className="p-3 md:p-4">
        <div className="flex flex-col space-y-2">
          <div className="flex justify-between items-start">
            <div className="pr-2">
              <h3 className={`font-medium ${isMobile ? 'text-base' : 'text-lg'} flex flex-wrap items-center`}>
                {derivative.title}
                <span className="inline-flex ml-2">{getStatusBadge()}</span>
              </h3>
              <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3 text-xs text-muted-foreground mt-1">
                <span className="flex items-center">
                  <Clock className="h-3 w-3 mr-1 shrink-0" />
                  {getDateInfo()}
                </span>
                {derivative.expressionType && (
                  <Badge variant="outline" className="text-xs w-fit bg-white text-gray-700 dark:text-primary font-bold border-gray-300 dark:border-primary/20">
                    #{derivative.expressionType}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Link to={`/derivative/${derivative.id}`}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <ExternalLink className="h-4 w-4" />
                  <span className="sr-only">View</span>
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card border border-border">
                  <DropdownMenuItem onClick={() => onEdit(derivative.id)}>
                    <Edit className="mr-2 h-4 w-4" />
                    <span>Edit</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDelete(derivative.id)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 text-xs md:text-sm">
            <Badge variant="outline">Based on: {derivative.originalTitle}</Badge>
            {derivative.playlistName && (
              <Badge variant="outline" className="bg-gray-100 dark:bg-primary/10">
                Playlist: {derivative.playlistName}
              </Badge>
            )}
          </div>

          {derivative.reviewComment && derivative.status === 'rejected' && (
            <div className="mt-1 border-l-4 border-destructive pl-3">
              <p className="text-xs md:text-sm text-destructive"><strong>Review Comment:</strong> {derivative.reviewComment}</p>
            </div>
          )}
          
          {derivative.social_links && (
            <div className="mt-2">
              <p className="text-xs text-muted-foreground mb-1">Social Links:</p>
              <SocialLinks socialLinks={derivative.social_links} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default DerivativeRow;
